import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { Formik, Form, FormikProps } from 'formik';
import { merge } from 'lodash';

import ListItemText from '@material-ui/core/ListItemText';
import ListItem from '@material-ui/core/ListItem';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import Info from 'components/Tooltip/Info';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import EditIcon from '@material-ui/icons/EditOutlined';
import Container from '@material-ui/core/Container';

import Background from 'components/Layout/Background';
import { pluralize } from 'helpers/helpers';
import { pluralizeYearsAndMonths } from 'components/Generic/YearsAndMonths';
import compose from 'helpers/compose';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import FormNumberField from 'components/FormFields/FormNumberField';
import Switch from 'components/FormFields/Switch';
import ControlButtons from 'components/ControlButtons';
import FormSelectField from 'components/FormFields/FormSelectField';

import { RootStore } from 'stores/rootStore';
import { SingleFeesData } from './Fees';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import useStyles from './styles';

import { WorkflowSingleFeesStore } from 'stores/workflowStores/workflowSingleFeesStore';
import { WorkflowBasicStore } from 'stores/workflowStores/workflowBasicStore/workflowBasicStore';
import SingleFeeItem from './SingleFeeItem';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import { Flows } from 'pages/Accounts/Detail/enums';
import { Currency } from 'enums';
import { AccountDetailStore } from 'stores/account/accountDetailStore';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  rootStore: RootStore;
  tabChange: Function;
  workflowBasicStore: WorkflowBasicStore;
  detailMode?: boolean;
  readOnly?: boolean;
  accountCurrency?: Currency;
  //flow?: Flows;
  detailStore?: AccountDetailStore;
}

const SingleFees: React.FC<Props> = ({
  rootStore,
  tabChange,
  workflowBasicStore,
  detailMode,
  detailStore,
  //flow,
  readOnly,
  accountCurrency,
}) => {
  //const detailMode = detailMode || !!detailStore;
  //if (detailMode) readOnly = true; // EDS-4915
  const flow = detailStore?.flow?.id;
  const classes = useStyles({});
  const { accountId } = useParams<Params>();
  const [store] = useState(new WorkflowSingleFeesStore(rootStore, accountId, detailMode ? ViewMode.DETAIL : undefined));
  const { t } = useTranslation();
  const currency = detailMode ? accountCurrency! : rootStore.stores.editAccountStore.currency;
  const translatedCurrency = t(`currency_${currency}`);
  const [editMode, setEditMode] = useState(!!detailMode && flow === Flows.PRICELIST && !readOnly);

  useEffect(() => {
    rootStore.stores.editAccountStore.setStep(5);
  }, [rootStore.stores.editAccountStore]);

  useEffect(() => {
    (async () => {
      await store.fetchFees();
      if (store.draft) setEditMode(true);
    })();
  }, [store]);

  const controlButtons = (editMode: boolean, formikProps: FormikProps<SingleFeesData>, top?: boolean) => {
    return (
      <>
        {!editMode ? (
          <div className={classes.buttons}>
            <Button
              color="primary"
              variant="contained"
              id="edit-regular-fees-button"
              onClick={() => {
                setEditMode(!editMode);
                flow === Flows.PLAN && store.setDraft();
              }}
            >
              <EditIcon fontSize="medium" />
              {t('accountDetail_edit_PRICELIST')}
            </Button>
          </div>
        ) : (
          flow !== Flows.PLAN && (
            <div className={classes.buttons}>
              <Button
                id="reset-regular-fees-button"
                color="default"
                variant="contained"
                onClick={() => {
                  store.fetchFees();
                  setEditMode(!editMode);
                }}
              >
                {t('general_cancel')}
              </Button>
              <Button
                color="primary"
                id="submit-regular-fees-button"
                variant="contained"
                onClick={async () => {
                  const ok = await store.handleSubmit(formikProps.values, formikProps);
                  if (ok) {
                    store.fetchFees();
                    setEditMode(!editMode);
                  }
                }}
              >
                {t('general_save')}
              </Button>
            </div>
          )
        )}
      </>
    );
  };

  return store.data !== null ? (
    <Formik
      initialValues={merge({}, { length: { years: 0, months: 0 } }, store.data)}
      onSubmit={() => {}}
      enableReinitialize
    >
      {(formikProps) => (
        <FormWithoutEnterSubmit>
          <Container maxWidth="md">
            <Background afterMenu={!flow}>
              <List className={`${classes.root} ${classes.singleFees}`}>
                {detailMode && formikProps.values.deposits && formikProps.values.prepaidRemaining && (
                  <>
                    <SingleFeeItem
                      text={t('fees_investments_sum')}
                      fee={formikProps.values.totalInvestments}
                      tooltip={t('tooltop_singleFees_investments_sum')}
                      currency={currency}
                    />
                    <SingleFeeItem
                      text={t('fees_deposits')}
                      fee={formikProps.values.deposits}
                      currency={currency}
                      tooltip={t('tooltop_singleFees_deposits')}
                    />
                    <SingleFeeItem
                      text={t('fees_prepaidRemaining')}
                      fee={formikProps.values.prepaidRemaining}
                      currency={currency}
                    />
                  </>
                )}
                {!detailMode && (
                  <>
                    <Divider />
                    <SingleFeeItem
                      text={t('fees_entryFee')}
                      className={`${classes.section} ${classes.em}`}
                      fee={
                        formikProps.values.individualFee
                          ? formikProps.values.individualFee
                          : formikProps.values.calculatedFee
                      }
                      currency={currency}
                    />
                    <List className={classes.nested}>
                      <SingleFeeItem
                        text={t('fees_investments_single')}
                        fee={formikProps.values.initialDeposit}
                        currency={currency}
                        tooltip={t('tooltop_singleFees_investments_single')}
                      />
                      <SingleFeeItem
                        text={
                          store.data!.periodicMonths?.years || store.data!.periodicMonths?.months
                            ? `${t('fees_investments_regularAmount')} (na ${pluralizeYearsAndMonths(
                                store.data!.periodicMonths?.years,
                                store.data!.periodicMonths?.months,
                              )})`
                            : t('fees_investments_regularAmount')
                        }
                        fee={formikProps.values.periodicDeposits}
                        currency={currency}
                        tooltip={t('tooltop_singleFees_regularAmount')}
                      />
                      {/*formikProps.values.employer && (
                        <>
                          {formikProps.values.clientInvestments && (
                            <SingleFeeItem
                              text={t('fees_investments_client')}
                              fee={formikProps.values.clientInvestments}
                              currency={currency}
                            />
                          )}
                          {formikProps.values.employerInvestments && (
                            <SingleFeeItem
                              text={t('fees_investments_employer')}
                              fee={formikProps.values.employerInvestments}
                              currency={currency}
                            />
                          )}
                        </>
                      )*/}
                      <SingleFeeItem
                        text={t('fees_investments_sum')}
                        fee={formikProps.values.totalInvestments}
                        tooltip={t('tooltop_singleFees_investments_sum')}
                        currency={currency}
                      />
                      <SingleFeeItem
                        text={t('fees_feeRate')}
                        rate={formikProps.values.feeRate}
                        tooltip={t('tooltop_singleFees_feeRate')}
                      />
                      <SingleFeeItem
                        text={t('fees_calculated_fee')}
                        fee={formikProps.values.calculatedFee}
                        currency={currency}
                        tooltip={t('tooltop_singleFees_calculated_fee')}
                      />
                      {editMode && formikProps.values.calculatedFee.value > 0 && (
                        <ListItem>
                          <Box className={classes.box}>
                            <Switch
                              name="prepaidIndividual"
                              label={t('fees_prepaidIndividual')}
                              fast={false}
                              fullWidth
                              afterChange={() => store.refresh(formikProps.values, formikProps)}
                            />
                          </Box>
                        </ListItem>
                      )}
                      {formikProps.values.prepaidIndividual && formikProps.values.calculatedFee.value > 0 && (
                        <>
                          {(editMode || formikProps.values.discount > 0) && (
                            <SingleFeeItem
                              text={t('fees_discount')}
                              rate={formikProps.values.discount || 0}
                              tooltip={t('tooltop_singleFees_discount')}
                            />
                          )}
                          {editMode ? (
                            <ListItem className={classes.itemBetween}>
                              <Box className={classes.box}>
                                <ListItemText primary={t('fees_individual_feeLabel')} />
                                <div className={classes.fee}>
                                  <FormNumberField
                                    name="individualFee.value"
                                    afterBlur={() => {
                                      store.refresh(formikProps.values, formikProps);
                                    }}
                                    endAdornment={translatedCurrency}
                                  />
                                </div>
                              </Box>
                              <Info
                                content={
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: t(`'tooltop_singleFees_individualFee'`, {
                                        currency: t('currency_' + currency),
                                        interpolation: { escapeValue: false },
                                      }),
                                    }}
                                  />
                                }
                              />
                            </ListItem>
                          ) : (
                            formikProps.values.discount > 0 && (
                              <SingleFeeItem
                                text={t('fees_individual_feeLabel')}
                                fee={formikProps.values.individualFee}
                                currency={currency}
                                className={`${classes.bold}`}
                                tooltip={t('tooltop_singleFees_individualFee')}
                              />
                            )
                          )}
                        </>
                      )}
                    </List>
                  </>
                )}

                <Divider />

                <SingleFeeItem
                  text={t('fees_contFee')}
                  detail={t('fees_contFee_detail')}
                  className={`${classes.section} ${classes.em} ${classes.maxWidth}`}
                  rate={
                    formikProps.values.continuousIndividual
                      ? formikProps.values.individualRate / 100
                      : formikProps.values.feeRate
                  }
                />
                <List className={classes.nested}>
                  <SingleFeeItem
                    text={t('fees_feeRate')}
                    rate={formikProps.values.feeRate}
                    tooltip={t('tooltop_singleFees_contFee')}
                  />
                  {editMode && (
                    <ListItem>
                      <Box className={classes.box}>
                        <Switch
                          name="continuousIndividual"
                          label={t('fees_continuousIndividual')}
                          fast={false}
                          fullWidth
                          afterChange={() => store.refresh(formikProps.values, formikProps)}
                        />
                      </Box>
                    </ListItem>
                  )}
                  {formikProps.values.continuousIndividual && (
                    <>
                      {(editMode || !!formikProps.values.rateDiscount) && (
                        <SingleFeeItem
                          text={t('fees_discount')}
                          rate={formikProps.values.rateDiscount}
                          tooltip={t('tooltop_singleFees_discountRate')}
                        />
                      )}
                      {editMode ? (
                        <ListItem className={classes.itemBetween}>
                          <Box className={classes.box}>
                            <ListItemText primary={t('fees_individual_rate')} />
                            <div className={classes.fee}>
                              <FormNumberField
                                name="individualRate"
                                afterBlur={() => {
                                  store.refresh(formikProps.values, formikProps);
                                }}
                                endAdornment={'%'}
                                maxLength={3}
                              />
                            </div>
                          </Box>
                          <Info content={t('tooltop_singleFees_individualRate')} />
                        </ListItem>
                      ) : (
                        formikProps.values.continuousIndividual && (
                          <SingleFeeItem
                            text={t('fees_individual_rate')}
                            rate={formikProps.values.individualRate / 100}
                            className={`${classes.bold}`}
                            tooltip={t('tooltop_singleFees_individualRate')}
                          />
                        )
                      )}
                    </>
                  )}
                  {editMode && (
                    <ListItem>
                      <Box className={classes.box}>
                        <Switch
                          name="cappingApplied"
                          label={t('fees_cappingApplied')}
                          fast={false}
                          fullWidth
                          afterChange={() => store.refresh(formikProps.values, formikProps)}
                        />
                      </Box>
                    </ListItem>
                  )}

                  {formikProps.values.cappingApplied && (
                    <>
                      {editMode ? (
                        <ListItem className={classes.itemBetween}>
                          <Box className={classes.box}>
                            <ListItemText primary={t('fees_cappedFee')} />
                            <div className={classes.fee}>
                              <FormNumberField
                                name="cappedFee.value"
                                afterBlur={() => {
                                  store.refresh(formikProps.values, formikProps);
                                }}
                                endAdornment={translatedCurrency}
                              />
                            </div>
                          </Box>
                          <Info content={t('tooltop_singleFees_cappedFee')} />
                        </ListItem>
                      ) : (
                        formikProps.values.cappedFee && (
                          <SingleFeeItem
                            text={t('fees_cappedFee')}
                            fee={formikProps.values.cappedFee}
                            currency={currency}
                            tooltip={t('tooltop_singleFees_cappedFee')}
                          />
                        )
                      )}
                    </>
                  )}
                </List>

                {!detailMode && (
                  <>
                    <Divider />

                    {editMode && (
                      <>
                        <SingleFeeItem
                          text={t('fees_calculationDetail')}
                          className={`${classes.section} ${classes.em}`}
                        />

                        <List className={classes.nested}>
                          <SingleFeeItem
                            text={t('fees_regularInvestment')}
                            fee={{
                              value: formikProps.values.periodicDeposit.value,
                              currency: formikProps.values.periodicDeposit.currency,
                            }}
                            currency={currency}
                            tooltip={
                              formikProps.values.employer ? t('tooltop_singleFees_regularPlusEmployer') : undefined
                            }
                          />
                          {editMode ? (
                            <ListItem>
                              <Box className={classes.box}>
                                <div style={{ width: '50%' }}>
                                  <FormSelectField
                                    name={`type`}
                                    label={t('fees_feeType')}
                                    options={store.data!.options.types.map((type) => ({
                                      value: type,
                                      label: t(`fees_feeType_${type}`),
                                    }))}
                                    afterChange={() => {
                                      if (formikProps.values.type === 'CONTINUOUS') {
                                        formikProps.setFieldValue('continuousIndividual', false);
                                        store.refresh(
                                          { ...formikProps.values, continuousIndividual: false },
                                          formikProps,
                                        );
                                      } else {
                                        store.refresh(formikProps.values, formikProps);
                                      }
                                    }}
                                    tooltipContent={
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: t('tooltop_singleFees_feeType', {
                                            interpolation: { escapeValue: false },
                                          }),
                                        }}
                                      />
                                    }
                                  />
                                </div>
                                {formikProps.values.type === 'PREPAID' && (
                                  <>
                                    <Typography style={{ width: '20%', textAlign: 'right' }}>
                                      {t('fees_feeType_PREPAID_length')}
                                    </Typography>
                                    <FormSelectField
                                      name={`length.years`}
                                      label={t('general_years')}
                                      options={store.data!.options.years.map((year) => ({
                                        value: `${year}`,
                                        label: `${year}`,
                                      }))}
                                      afterChange={() => {
                                        store.refresh(formikProps.values, formikProps);
                                      }}
                                      style={{ width: '10%' }}
                                    />
                                    <FormSelectField
                                      name={`length.months`}
                                      label={t('general_months')}
                                      options={Array(12)
                                        .fill(0)
                                        .map((_x, i) => ({
                                          value: `${i}`,
                                          label: `${i}`,
                                        }))}
                                      afterChange={() => {
                                        store.refresh(formikProps.values, formikProps);
                                      }}
                                      style={{ width: '10%' }}
                                    />
                                  </>
                                )}
                              </Box>
                            </ListItem>
                          ) : (
                            <SingleFeeItem
                              text={t('fees_feeType')}
                              longValue
                              value={
                                t(`fees_feeType_${formikProps.values.type}`) +
                                (formikProps.values.type === 'PREPAID'
                                  ? ` (${pluralizeYearsAndMonths(store.data!.length.years, store.data!.length.months)})`
                                  : '')
                              }
                            />
                          )}
                          {formikProps.values.type === 'PREPAID' && (
                            <SingleFeeItem
                              text={t('fees_investments_regular')}
                              fee={formikProps.values.periodicDeposits}
                              currency={currency}
                              tooltip={t('tooltop_singleFees_investments_regular')}
                            />
                          )}
                        </List>
                        {formikProps.values.type === 'CONTINUOUS' && (
                          <List className={classes.nested}>
                            <SingleFeeItem
                              text={t('fees_feeRegularRate')}
                              rate={
                                formikProps.values.continuousIndividual
                                  ? formikProps.values.individualRate / 100
                                  : formikProps.values.feeRate
                              }
                              tooltip={t('tooltop_singleFees_feeRegularRate')}
                            />
                            <SingleFeeItem
                              text={t('fees_regularCalculatedFee')}
                              fee={formikProps.values.regularCalculatedFee}
                              currency={currency}
                              tooltip={t('tooltop_singleFees_regularCalculatedFee')}
                            />
                            <SingleFeeItem
                              text={`${t('fees_totalRegularCalculatedFee')} ${
                                store.data!.defaultPlanningYears
                              } ${pluralize(
                                store.data!.defaultPlanningYears,
                                t('general_year'),
                                t('general_years_234'),
                                t('general_years'),
                              )}`}
                              fee={formikProps.values.totalRegularCalculatedFee}
                              currency={currency}
                            />
                          </List>
                        )}
                      </>
                    )}
                    <Divider />

                    <SingleFeeItem
                      text={t('fees_closing_fee')}
                      //nf rate={formikProps.values.closingFee}
                      fee={formikProps.values.closingFee}
                      currency={currency}
                      className={classes.section}
                    />

                  </>
                )}
              </List>
              {((!store.draft && flow === Flows.PLAN) || !detailMode) && controlButtons(editMode, formikProps)}
            </Background>
          </Container>

          {!readOnly && (
            <div className={classes.controlButtons}>
              <ControlButtons
                isSubmitting={formikProps.isSubmitting}
                onNext={() =>
                  store.handleNext(
                    formikProps.values,
                    formikProps,
                    tabChange,
                    flow === Flows.PLAN && !store.draft,
                    detailStore?.goFwd
                  )
                }
                nextText={t('fees_regular_title')}
                onBack={() => store.handleBack(detailStore?.goBack)}
                //backText={t('accountStepper_planProposal')}
                backText={t(detailStore?.prevStepLabel || '')}
                disabled={editMode && !detailMode}
                //hideBack={detailMode && flow === Flows.PRICELIST}
                hideBack={detailStore?.firstStep}
              />
            </div>
          )}
        </FormWithoutEnterSubmit>
      )}
    </Formik>
  ) : null;
};

export default compose(inject('rootStore'), observer)(SingleFees);
